import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../../_helpers/axios_instance';
import { format } from 'date-fns';

export const getCmeCredits = createAsyncThunk(
  'cmeCredits/fetchCredits',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`/api/cme_credits/`);
      return response.data;
    } catch (error) {
      console.error("ERROR CLAIMING CME CREDIT", error);
      if (error.response) {
        return rejectWithValue(error.response.data.errors);
      }
      return rejectWithValue(error.message);
    }
  },
  {
    condition: (thunkArg, { getState }) => {
      const { cme_credits } = getState();
      const { fetchCreditsStatus } =  cme_credits;
      return fetchCreditsStatus !== 'loading';
    },
  }
);

export const claimCmeCredit = createAsyncThunk(
  'cmeCredits/claimCredit',
  // The first argument to the payload creator is what we pass when we dispatch the thunk
  async (creditData, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`/api/cme_credits/`, {
        lesson_number: creditData.content_id,
        certificate_type: creditData.certificate_type,
        credential_type: creditData.credential_type,
        bias_response: creditData.bias_response,
        credits_awarded: creditData.credits_awarded,
        title_of_activity: creditData.title_of_activity,
        cme_certificate_awarded_at: format(new Date(), 'yyyy-MM-dd'),
      });
      return response.data;
    } catch (error) {
      console.error("ERROR CLAIMING CME CREDIT", error);
      return rejectWithValue(error.response?.data?.errors || error.message);
    }
  },
  {
    condition: (_, { getState }) => {
      return getState().cme_credits?.claimCreditStatus !== 'loading';
    },
  }
);

export const downloadCertificate = createAsyncThunk('cmeCredits/downloadCertificate',
  async ({credit_id, filename}, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`/api/cme_credits/certificate/${credit_id}`,{responseType: 'blob'})

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${filename}`);
      document.body.appendChild(link);
      link.click();
      // return ;
    } catch (error) {
      console.error("ERROR CLAIMING CME CREDIT", error);
      return rejectWithValue(error.response?.data?.errors || error.message);
    }
  },
  {
    condition: (_, { getState }) => {
      return getState().cme_credits?.downloadCertificateStatus !== 'loading';
    },
  }
)

const initialState = {
  credits: [],
  fetchCreditsStatus: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed'
  fetchCreditsError: null,
  claimCreditStatus: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed'
  claimCreditError: null,
  downloadCertificateStatus: 'idle',
  downloadCertificateError: null,
};


const cmeCreditsSlice = createSlice({
  name: 'cme_credits',
  initialState,
  reducers: {},
  // Use `extraReducers` to handle actions from outside the slice
  extraReducers: (builder) => {
    builder
    // Case for when the async operation is pending
    .addCase(getCmeCredits.pending, (state) => {
      state.fetchCreditsStatus = 'loading';
      state.fetchCreditsError = null; // Clear previous errors
    })
    // Case for when the operation is successful
    .addCase(getCmeCredits.fulfilled, (state, action) => {
      state.fetchCreditsStatus = 'succeeded';
      // `action.payload` is the data returned from the async function
      state.credits = action.payload;
    })
    // Case for when the operation fails
    .addCase(getCmeCredits.rejected, (state, action) => {
      state.fetchCreditsStatus = 'failed';
      // `action.payload` is the value from `rejectWithValue`
      state.fetchCreditsError = action.payload;
    })
    .addCase(claimCmeCredit.pending, (state) => {
      state.claimCreditStatus = 'loading';
      state.claimCreditError = null;
    })
    .addCase(claimCmeCredit.fulfilled, (state, action) => {
      state.claimCreditStatus = 'succeeded';
      state.credits.push(action.payload);
    })
    .addCase(claimCmeCredit.rejected, (state, action) => {
      state.claimCreditStatus = 'failed';
      state.claimCreditError = action.payload;
    })
    .addCase(downloadCertificate.pending, (state) => {
      state.downloadCertificateStatus = 'loading';
      state.downloadCertificateError = null;
    })
    .addCase(downloadCertificate.rejected, (state, action) => {
      state.downloadCertificateStatus = 'failed';
      state.downloadCertificateError = action.payload;
    })
    .addCase(downloadCertificate.fulfilled, (state) => {
      state.downloadCertificateStatus = 'succeeded';
      state.downloadCertificateError = null;
    });
  },
});

export default cmeCreditsSlice.reducer;

export const selectAllCmeCredits = (state) => state.cme_credits.credits;
export const selectCmeCreditsStatus = (state) => state.cme_credits.fetchCreditsStatus;
export const selectCmeCreditsError = (state) => state.cme_credits.fetchCreditsError;
export const selectClaimCreditStatus = (state) => state.cme_credits.claimCreditStatus;
export const selectClaimCreditError = (state) => state.cme_credits.claimCreditError;

export const selectAllCmeCreditsForContent = (state, record_id) => {
  const {credits} = state.cme_credits;
  return credits.filter(
    (credit) => credit.lesson_number === record_id
  );
}
export const selectLatestCmeCreditForContent = (state, record_id) => {
  const matchingCredits = selectAllCmeCreditsForContent(state, record_id);
  if (matchingCredits.length > 0) {
    return matchingCredits.reduce((latest, current) => {
      return latest.created_at > current.created_at ? latest : current;
    });
  }
  return null;
};